// Notification_Preference_Modal.jsx
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Modal, Switch } from "@/config/antd";
import { CloseOutlined, RightOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import DisabledNotificationsModal from "@/pages/settings/components/notification_preference/Disabled_Notifications_Modal";
import NotificationFrequencyModal from "@/pages/settings/components/notification_preference/Notification_Frequency_Modal";
import SuccessAlert from "@/components/common/alerts/Success";
import ErrorAlert from "@/components/common/alerts/Error";
import {
  updateProfileAsync,
  selectIsPushEnabled,
} from "@/store/redux/slices/user/user_slice";
import { useOneSignalContext } from "@/context/OneSignalContext";

const NotificationPreferenceModal = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const isPushEnabledFromRedux = useSelector(selectIsPushEnabled);
  const { initialized, isSubscribed, optIn, optOut } = useOneSignalContext();

  const [showDisabledNotificationsModal, setShowDisabledNotificationsModal] =
    useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [promotionsEnabled, setPromotionsEnabled] = useState(true);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Seed isPushEnabled from Redux each time the modal opens.
  useEffect(() => {
    if (isVisible) {
      setPushEnabled(isPushEnabledFromRedux);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleOptionClick = (optionId) => {
    if (optionId === "disabled_notifications") {
      setShowDisabledNotificationsModal(true);
    } else if (optionId === "frequency") {
      setShowFrequencyModal(true);
    }
  };

  const handlePromotionSwitchChange = (checked) => {
    setPromotionsEnabled(checked);
  };


  const handlePushToggle = async (checked) => {
    setPushLoading(true);
    try {
      await dispatch(updateProfileAsync({ isPushEnabled: checked })).unwrap();

      // Apply confirmed state only after server success.
      setPushEnabled(checked);

      // IMPORTANT:
      // `isPushEnabled` is the server preference. To actually receive pushes,
      // the *browser device* must also be opted in at OneSignal level.
      if (checked) {
        if (!isSubscribed && initialized) {
          await optIn();
        }
      } else {
        if (isSubscribed && initialized) {
          await optOut();
        }
      }

      setShowSuccess(true);
    } catch (err) {
      setErrorMessage(
        typeof err === "string" ? err : "Failed to update push notification setting."
      );
      setShowError(true);
    } finally {
      setPushLoading(false);
    }
  };

  // First two notification options that navigate to other modals
  const navigationOptions = [
    {
      id: "disabled_notifications",
      title: "Toggle Notification Alerts",
      description: "Notifications that you want to see",
      icon: "SmsNotification",
    },
    {
      id: "frequency",
      title: "Frequency",
      description: "How often do you want to be notified",
      icon: "SmsNotification",
    },
  ];

  const promotionOption = {
    id: "promotions",
    title: "Promotions and Alerts",
    description: "Whenever we have deals and offers for you",
    icon: "StarNotification",
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 flex items-center justify-between border-b border-gray-200">
            <h2 className="text-bold-x text-primary">Notification Setup</h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            <div className="space-y-4">
              {/* Navigation options */}
              {navigationOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option.id)}
                  className="flex items-center justify-between p-4 bg-white rounded-lg cursor-pointer transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <WebIcon icon={option.icon} className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-bold text-black">{option.title}</h3>
                      <p className="text-small dark-mode-text">
                        {option.description}
                      </p>
                    </div>
                  </div>
                  <RightOutlined className="text-gray-400" />
                </div>
              ))}

              {/* Push Notifications toggle (server-side isPushEnabled flag) */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <WebIcon icon="SmsNotification" className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-bold text-black">Push Notifications</h3>
                    <p className="text-small dark-mode-text">
                      Enable push alerts sent to this device
                    </p>
                  </div>
                </div>
                <Switch
                  checked={pushEnabled}
                  onChange={handlePushToggle}
                  loading={pushLoading}
                />
              </div>

              {/* Promotions and Alerts toggle (local preference) */}
              <div
                key={promotionOption.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <WebIcon icon={promotionOption.icon} className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-bold text-black">
                      {promotionOption.title}
                    </h3>
                    <p className="text-small dark-mode-text">
                      {promotionOption.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={promotionsEnabled}
                  onChange={handlePromotionSwitchChange}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Disabled Notifications Modal */}
      <DisabledNotificationsModal
        isVisible={showDisabledNotificationsModal}
        onClose={() => setShowDisabledNotificationsModal(false)}
      />

      {/* Frequency Modal */}
      <NotificationFrequencyModal
        isVisible={showFrequencyModal}
        onClose={() => setShowFrequencyModal(false)}
      />

      {/* Success feedback for isPushEnabled toggle */}
      <SuccessAlert
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
        header="Setting Updated"
        paragraph="Your push notification preference has been saved."
        buttonText="Done"
        onButtonClick={() => setShowSuccess(false)}
      />

      {/* Error feedback for isPushEnabled toggle */}
      <ErrorAlert
        isVisible={showError}
        onClose={() => setShowError(false)}
        header="Update Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
        handleRecovery={() => setShowError(false)}
      />
    </>
  );
};

NotificationPreferenceModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default NotificationPreferenceModal;
